import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, Compass, Network, GraduationCap, Radio, Megaphone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

export default function StrongerTogetherPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0A2037] via-[#0d2a45] to-[#079897] pt-36 pb-20">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[520px] h-[520px] bg-teal-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-amber-400/8 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/40 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" aria-hidden="true" />
              <span className="text-amber-200 text-[11px] font-bold tracking-[0.2em] uppercase">DCELC Initiative</span>
            </div>
            <h1
              className="text-white font-bold leading-[0.95] mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(48px, 8vw, 84px)' }}
              data-testid="stronger-together-page-title"
            >
              Stronger <span className="text-teal-300 italic">Together</span>
            </h1>
            <p className="text-teal-200 text-sm sm:text-base font-medium tracking-wide mb-8 max-w-2xl">
              A DCELC Initiative for Collective Leadership in Early Childhood Education
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="inline-flex items-baseline gap-2 px-4 py-2 rounded-xl bg-white/5 ring-1 ring-teal-400/40 backdrop-blur-sm">
                <span className="text-teal-300 font-black text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>70+</span>
                <span className="text-slate-300 text-xs uppercase tracking-widest font-semibold">Leaders Convened, May 2026</span>
              </div>
              <div className="inline-flex items-baseline gap-2 px-4 py-2 rounded-xl bg-white/5 ring-1 ring-amber-400/40 backdrop-blur-sm">
                <span className="text-amber-300 font-black text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>1</span>
                <span className="text-slate-300 text-xs uppercase tracking-widest font-semibold">Field. One Voice.</span>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-teal-300/30 backdrop-blur-sm">
              <ArrowRight className="w-3.5 h-3.5 text-teal-300" aria-hidden="true" />
              <span className="text-teal-200 text-[11px] font-bold tracking-[0.22em] uppercase">From a Convening to a Movement</span>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl bg-lime-400/10 border border-lime-300/40 px-5 py-4 max-w-2xl" data-testid="strong-beginnings-callout">
              <div className="flex-1 min-w-[220px]">
                <p className="text-lime-200 text-[11px] font-black tracking-[0.2em] uppercase mb-1">New · Strong Beginnings Video Series</p>
                <p className="text-slate-200 text-sm">Share your program's story in a professionally produced video — 16 programs, all 8 wards. Applications close November 20, 2026.</p>
              </div>
              <button onClick={() => navigate('/strong-beginnings')} data-testid="strong-beginnings-apply-link"
                className="inline-flex items-center gap-2 px-6 py-3 bg-lime-400 hover:bg-lime-300 text-[#0A2037] font-bold rounded-full text-sm transition-colors focus:outline-none focus:ring-4 focus:ring-lime-300/50">
                Apply Now <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        {/* Story + video */}
        <section className="py-20 bg-[#0a1628]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6 space-y-5">
              <p className="text-slate-100 text-lg leading-relaxed">
                In <strong className="text-teal-300">May 2026</strong>, more than 70 early childhood leaders, educators, and advocates gathered for <em className="text-amber-200 not-italic font-semibold">Stronger Together: Protecting Progress Through Collective Leadership</em> &mdash; a convening born from a simple but urgent recognition: <span className="text-white font-semibold">Washington, DC&apos;s early childhood education field is strongest when it moves as one.</span>
              </p>
              <p className="text-slate-300 text-base leading-relaxed">
                That single gathering is now becoming something bigger. <strong className="text-teal-200">Stronger Together</strong> is DCELC&apos;s ongoing initiative to protect progress in DC&apos;s early childhood field through collective leadership.
              </p>
              <div className="rounded-xl bg-white/5 border border-teal-300/30 backdrop-blur-sm p-5">
                <div className="flex items-center gap-2 text-teal-300 text-[10px] font-bold tracking-[0.2em] uppercase mb-2">
                  <Compass className="w-3.5 h-3.5" aria-hidden="true" />
                  Core Belief
                </div>
                <p className="text-white text-sm font-medium leading-snug">
                  Leadership doesn&apos;t happen by accident. It has to be <span className="text-teal-300">cultivated</span>, <span className="text-teal-300">trained</span>, and <span className="text-teal-300">resourced</span>.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-xl overflow-hidden border border-white/15 aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/OhBEWiB0xpU"
                  title="Stronger Together: Protecting Progress Through Collective Leadership — convening recap video"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="stronger-together-recap-video"
                />
              </div>
              <p className="text-slate-400 text-xs mt-2 text-center">Watch the May 2026 convening recap</p>
            </div>
          </div>
        </section>

        {/* Connective infrastructure */}
        <section className="py-20 bg-gradient-to-b from-[#0a1628] to-[#0d2137]" data-testid="stronger-together-infrastructure">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-amber-500/15 text-amber-300 rounded-full text-sm font-semibold mb-4">
                One Vision, One Infrastructure
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                The Backbone of ECE as a Public Good
              </h2>
              <p className="text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Stronger Together is the connective infrastructure of the &ldquo;ECE as a Public Good&rdquo; campaign. Its
                convening, leadership programming, and communications platform bring ECE directors, practitioners, and City
                leaders into a shared, data-informed narrative on treating early learning as an essential, equitably
                financed public good.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Network className="w-6 h-6 text-teal-300" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Convening</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Bringing ECE directors, practitioners, and City leaders together in shared spaces to align around a
                  common, data-informed narrative for the field.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                  <GraduationCap className="w-6 h-6 text-amber-300" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Leadership Programming</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Practitioners who move through Stronger Together become messengers and validators for the affordability,
                  accessibility, and quality case DCELC makes to City leaders.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
                <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Radio className="w-6 h-6 text-indigo-300" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Communications Platform</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Advancing non-lobbying policy education and public awareness that reframes early learning as an
                  essential, equitably financed public good.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-l-4 border-teal-400 bg-white/5 rounded-r-2xl p-6">
                <h4 className="font-bold text-white mb-2">Reinforcing, Not Competing</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Leadership development and public advocacy reinforce rather than compete for capacity — every educator
                  equipped through Stronger Together strengthens the campaign&#39;s voice with City leaders.
                </p>
              </div>
              <div className="border-l-4 border-amber-400 bg-white/5 rounded-r-2xl p-6">
                <h4 className="font-bold text-white mb-2">Coherence Across a Fragmented System</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  This strengthens coherence and alignment across DC&#39;s fragmented, mixed-delivery ECE ecosystem — child
                  development homes, centers, schools, and community organizations moving in the same direction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Work Matters */}
        <section className="py-20 bg-[#0d2137]" data-testid="stronger-together-why-matters">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <h2
                className="text-white font-bold text-3xl sm:text-4xl leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Why This Work<br /><span className="text-amber-300">Matters</span>
              </h2>
              <div className="mt-6 rounded-xl border-l-4 border-amber-300 bg-amber-400/10 backdrop-blur-sm px-5 py-4">
                <p className="text-amber-100 text-sm leading-relaxed">
                  <strong className="text-amber-200">Progress in ECE has always depended on people willing to lead</strong> &mdash; to testify, to organize, to speak up for children, families, and the workforce.
                </p>
                <p className="mt-3 text-amber-100/90 text-sm leading-relaxed">
                  But leadership is not a trait some people have and others don&apos;t. It&apos;s a <span className="text-white font-semibold">capacity that grows when it&apos;s invested in.</span>
                </p>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <p className="text-slate-200 text-base leading-relaxed">
                Too often, the field has asked its educators and providers to lead without ever building the <strong className="text-teal-300">pipeline, training, or support structure</strong> to help them do it well or sustain it over time.
              </p>
              <p className="text-slate-300 text-base leading-relaxed">
                Where the field has often relied on <em className="text-slate-400">episodic collaboration</em>, coming together during a budget crisis, a subsidy rate fight, or a licensing change, then dispersing once the moment passes, Stronger Together builds the <span className="text-white font-semibold">leadership base that holds between those moments</span>, so protecting progress isn&apos;t dependent on a handful of the same people carrying the field forward again and again.
              </p>
              <p className="text-slate-300 text-base leading-relaxed">
                DC&apos;s early childhood sector includes <strong className="text-teal-200">hundreds of family child care providers, center-based programs, and community organizations</strong>, each carrying deep expertise and a shared stake in the systems that shape their work, including subsidy reimbursement, pay equity, facility standards, workforce development, and public investment in the Birth-to-Three continuum. But that expertise is too often scattered.
              </p>
              <p className="text-slate-200 text-base leading-relaxed border-l-2 border-teal-400 pl-4">
                Sustained progress depends on a <strong className="text-white">deep, growing bench of leaders</strong> across every level of the field, including classroom educators, family child care providers, center directors, and association staff, with the skills, confidence, and support to carry the work forward.
              </p>
            </div>
          </div>
        </section>

        {/* ECE as a Public Good sub-page CTA */}
        <section className="py-20 bg-[#0a1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-gradient-to-r from-indigo-600/20 via-indigo-500/10 to-teal-500/15 border border-indigo-400/30 p-8 sm:p-12 text-center" data-testid="stronger-together-ece-cta">
              <div className="w-14 h-14 bg-indigo-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Megaphone className="w-7 h-7 text-indigo-300" aria-hidden="true" />
              </div>
              <p className="text-indigo-300 text-xs font-bold uppercase tracking-[0.3em] mb-3">The Campaign</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                ECE as a Public Good
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed mb-7">
                The public-facing campaign that Stronger Together powers — reframing early childhood education as
                essential to Washington, DC&#39;s future, with the data, stories, and strategies behind the case for an
                equitably financed public good.
              </p>
              <Button
                onClick={() => navigate('/programs/ece-public-good')}
                className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold px-8 py-6 text-base"
                data-testid="stronger-together-ece-link"
              >
                Explore the Campaign
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Button>
            </div>

            {/* Stay tuned */}
            <div className="mt-10 rounded-2xl bg-gradient-to-r from-teal-500/15 via-teal-400/10 to-amber-400/15 border border-teal-300/30 backdrop-blur-sm p-6 sm:p-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 mb-4">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                <span className="text-white/90 text-[10px] font-bold tracking-[0.22em] uppercase">Coming Soon</span>
              </div>
              <h3 className="text-white font-bold text-2xl sm:text-3xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Stay Tuned.
              </h3>
              <p className="text-slate-200 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
                Cohort tracks, leadership pathways, training opportunities, and how to get involved &mdash; announced soon.
              </p>
              <Button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 500);
                }}
                className="mt-6 bg-teal-500 hover:bg-teal-400 text-[#0A2037] font-bold px-6 py-3 shadow-lg shadow-teal-500/30"
                data-testid="stronger-together-page-stay-in-touch"
              >
                Stay in Touch
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
